import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, DollarSign, Clock, Compass, Printer } from "lucide-react";
import { api, getDestinations } from "../api";
import { useI18n, useL } from "../i18n";
import SocialShare from "./SocialShare";
import useSocialMeta from "../hooks/useSocialMeta";

export default function SharedTripPage() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const L = useL();
  const [trip, setTrip] = useState(null);
  const [destMap, setDestMap] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/itineraries/${id}`).then((r) => setTrip(r.data)).catch(() => setError("not_found"));
    getDestinations({}).then((list) => setDestMap(Object.fromEntries(list.map((d) => [d.id, d]))));
  }, [id]);

  const firstDest = trip?.stops?.[0] && destMap[trip.stops[0].destination_id];
  useSocialMeta({
    title: trip ? `${trip.title} · Rahhal` : "Rahhal — Trip",
    description: trip
      ? L({
          ar: `رحلة ${trip.days} ليلة إلى ${trip.stops.length} وجهة، بميزانية تقديرية $${Math.round(trip.total_usd)}. اكتشفها على رحّال.`,
          en: `A ${trip.days}-night trip across ${trip.stops.length} destinations, estimated at $${Math.round(trip.total_usd)}. Discover it on Rahhal.`,
          fr: `Un voyage de ${trip.days} nuits vers ${trip.stops.length} destinations, estimé à $${Math.round(trip.total_usd)}. Découvrez-le sur Rahhal.`
        })
      : "",
    image: firstDest?.image_url,
  });

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-[#78716C]">{L({ ar: "الرحلة غير موجودة", en: "Trip not found", fr: "Voyage introuvable" })}</p>
        <Link to="/" className="mt-4 inline-flex px-5 py-2.5 rounded-full bg-[#1C1917] text-[#FAF7F2] text-sm">
          {t("hero_cta_explore")}
        </Link>
      </div>
    );
  }

  if (!trip) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#78716C]">Loading…</div>;

  const shareText = L({
    ar: `شاهد رحلتي ${trip.title} على رحّال — تكلفة تقديرية $${Math.round(trip.total_usd)}`,
    en: `Check out my trip "${trip.title}" on Rahhal — est. $${Math.round(trip.total_usd)}`,
    fr: `Découvrez mon voyage "${trip.title}" sur Rahhal — env. $${Math.round(trip.total_usd)}`
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="shared-trip">
      <div className="rounded-3xl bg-gradient-to-br from-[#1C1917] to-[#57534E] text-[#FAF7F2] p-8 lg:p-10">
        <div className="text-[11px] uppercase tracking-widest opacity-70">
          {L({ ar: "رحلة مشاركة عبر رحّال", en: "Shared trip on Rahhal", fr: "Voyage partagé sur Rahhal" })}
        </div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl">{trip.title}</h1>
        <div className="mt-4 flex flex-wrap gap-5 text-sm">
          <div className="flex items-center gap-1.5 opacity-90"><Clock className="h-4 w-4" />{trip.days} {L({ ar: "ليلة", en: "nights", fr: "nuits" })}</div>
          <div className="flex items-center gap-1.5 opacity-90"><MapPin className="h-4 w-4" />{trip.stops.length} {L({ ar: "وجهة", en: "stops", fr: "étapes" })}</div>
          <div className="flex items-center gap-1.5 font-medium"><DollarSign className="h-4 w-4 text-[#D97706]" />${Math.round(trip.total_usd)}</div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => window.print()}
          data-testid="print-shared-trip"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#E6DED3] hover:border-[#1C1917] text-sm font-medium transition-colors no-print"
        >
          <Printer className="h-4 w-4" />
          {L({ ar: "طباعة / حفظ PDF", en: "Print / Save PDF", fr: "Imprimer / PDF" })}
        </button>
        <SocialShare text={shareText} testidPrefix="shared-trip-share" />
      </div>

      <div className="mt-6 space-y-3">
        {trip.stops.map((stop, i) => {
          const d = destMap[stop.destination_id];
          if (!d) return null;
          const city = lang === "ar" ? d.city_ar : d.city_en;
          return (
            <Link
              key={stop.destination_id}
              to={`/destination/${d.id}`}
              className="block rounded-2xl bg-white border border-[#E6DED3] p-4 flex items-center gap-4 hover:border-[#1C1917] transition-colors"
            >
              <img src={d.image_url} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#C85A32]">{t("day")} {i + 1}</div>
                <div className="font-display text-lg text-[#1C1917]">{city}</div>
              </div>
              <div className="text-sm font-medium text-[#1C1917] shrink-0">${d.avg_daily_cost_usd}/{t("per_day")}</div>
            </Link>
          );
        })}
      </div>

      <div className="mt-8 rounded-3xl bg-[#F3EDE4] p-6 text-center">
        <Compass className="h-8 w-8 text-[#C85A32] mx-auto" />
        <h3 className="mt-3 font-display text-xl text-[#1C1917]">
          {L({ ar: "خطّط رحلتك الخاصة الآن", en: "Plan your own trip now", fr: "Planifiez votre propre voyage" })}
        </h3>
        <Link to="/" className="mt-4 inline-flex px-5 py-2.5 rounded-full bg-[#1C1917] hover:bg-[#C85A32] text-[#FAF7F2] text-sm transition-colors">
          {t("hero_cta_explore")}
        </Link>
      </div>
    </div>
  );
}
